<template>
  <div class="signed-invoice-workflow">
    <div class="d-flex align-items-start justify-content-between mb-2">
      <div>
        <h6 class="mb-50">Download &amp; signed invoice</h6>
        <p class="text-muted small mb-0">
          Download the invoice, add your signature offline, then upload the signed PDF.
          Uploads are saved on the server for this invoice — not in the browser.
        </p>
      </div>
      <b-badge v-if="hasSignedCopy" variant="success" class="ml-2 flex-shrink-0">
        Signed copy on file
      </b-badge>
      <b-badge v-else variant="secondary" class="ml-2 flex-shrink-0">
        Awaiting signed copy
      </b-badge>
    </div>

    <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 0.5rem;">
      <span
        v-b-tooltip.hover="downloadTooltipOpts"
      >
        <b-button
          size="sm"
          variant="outline-primary"
          :disabled="downloading || !resolvedInvoiceId || !canDownload"
          @click="downloadInvoice"
        >
          <b-spinner v-if="downloading" small class="mr-50" />
          <feather-icon v-else icon="DownloadIcon" size="14" class="mr-50" />
          Download Invoice
        </b-button>
      </span>
      <b-button
        size="sm"
        variant="primary"
        :disabled="uploading || !resolvedInvoiceId"
        @click="openFilePicker"
      >
        <b-spinner v-if="uploading" small class="mr-50" />
        <feather-icon v-else icon="UploadIcon" size="14" class="mr-50" />
        Upload Signed Invoice
      </b-button>
      <input
        ref="signedFileInput"
        type="file"
        accept="application/pdf,.pdf"
        class="d-none"
        @change="onSignedFileSelected"
      />
    </div>

    <b-alert v-if="errorMessage" variant="danger" show class="small py-1 px-2 mb-2">
      {{ errorMessage }}
    </b-alert>
    <b-alert v-if="successMessage" variant="success" show class="small py-1 px-2 mb-2">
      {{ successMessage }}
    </b-alert>

    <div v-if="loadingHistory" class="text-muted small">
      <b-spinner small class="mr-50" /> Loading signed file history…
    </div>
    <div v-else-if="history.length" class="signed-invoice-history">
      <div class="small font-weight-bold text-muted mb-1">Uploaded signed files</div>
      <ul class="list-unstyled mb-0">
        <li
          v-for="(item, idx) in history"
          :key="item.id || item.file_path || item.url || idx"
          class="d-flex align-items-center justify-content-between py-1 border-bottom"
        >
          <div class="pr-2 min-w-0">
            <div class="text-truncate small font-weight-medium">
              {{ displayName(item) }}
            </div>
            <small class="text-muted">
              {{ formatWhen(item) }}
              <span v-if="item.uploaded_by || item.uploadedBy"> · {{ item.uploaded_by || item.uploadedBy }}</span>
            </small>
          </div>
          <b-button
            v-if="fileHref(item)"
            size="sm"
            variant="link"
            class="p-0 flex-shrink-0"
            :href="fileHref(item)"
            target="_blank"
            rel="noopener"
          >
            Open
          </b-button>
        </li>
      </ul>
    </div>
    <p v-else class="text-muted small mb-0">No signed invoice uploaded for this invoice yet.</p>
  </div>
</template>

<script>
import { BButton, BBadge, BSpinner, BAlert, VBTooltip } from 'bootstrap-vue'
import moment from 'moment'
import enhancedInvoiceService from '@/services/enhanced-invoice-service'
import JobService from '@/services/job-service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import {
  buildInvoiceDownloadFilename,
  getInvoiceFileSizeError,
} from '@/utils/invoice-filename'
import { resolveInvoiceApiKey } from '@/utils/invoice-api-key'
import { INVOICE_DOWNLOAD_DENIED_TOOLTIP } from '@/utils/invoice-permissions'

export default {
  name: 'SignedInvoiceWorkflow',
  components: { BButton, BBadge, BSpinner, BAlert },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    invoiceId: { type: [String, Number], default: null },
    invoice: { type: Object, default: null },
    /** Optional: parent generates PDF client-side and returns { blob, filename }. */
    clientPdfProvider: { type: Function, default: null },
    canDownload: { type: Boolean, default: true },
    /** When false (Create Invoice post-submit), hide hover tooltips. */
    showTooltips: { type: Boolean, default: true },
  },
  data() {
    return {
      history: [],
      loadingHistory: false,
      downloading: false,
      uploading: false,
      errorMessage: '',
      successMessage: '',
      downloadDeniedTooltip: INVOICE_DOWNLOAD_DENIED_TOOLTIP,
    }
  },
  computed: {
    resolvedInvoiceId() {
      return resolveInvoiceApiKey(this.invoiceId) || resolveInvoiceApiKey(this.invoice)
    },
    downloadTooltipOpts() {
      if (!this.showTooltips) return { disabled: true }
      return this.canDownload ? 'Download invoice PDF' : this.downloadDeniedTooltip
    },
    hasSignedCopy() {
      if (this.history.length) return true
      const inv = this.invoice || {}
      return !!(
        inv.has_signed_invoice
        || inv.signed_invoice
        || inv.signed_invoice_path
        || inv.signed_pdf
      )
    },
  },
  watch: {
    resolvedInvoiceId: {
      immediate: true,
      handler(id) {
        if (id) this.loadHistory()
        else {
          this.history = []
        }
      },
    },
  },
  methods: {
    displayName(item) {
      return item.file_name
        || item.filename
        || item.original_name
        || item.name
        || item.document_name
        || 'Signed invoice.pdf'
    },
    formatWhen(item) {
      const raw = item.uploaded_at || item.created_at || item.createdAt || item.updated_at
      if (!raw) return '—'
      try {
        return moment(raw).format('DD-MMM-YYYY HH:mm')
      } catch (_) {
        return String(raw)
      }
    },
    fileHref(item) {
      const url = item.url || item.file_url || item.download_url
      if (url && /^https?:\/\//i.test(url)) return url
      const path = item.file_path || item.path
      if (!path) return null
      if (/^https?:\/\//i.test(path)) return path
      return null
    },
    async loadHistory() {
      const id = this.resolvedInvoiceId
      if (!id) return
      this.loadingHistory = true
      this.errorMessage = ''
      try {
        const res = await enhancedInvoiceService.listSignedInvoices(id)
        if (res && res.status === 'OK') {
          this.history = Array.isArray(res.data) ? res.data : []
        } else {
          this.history = []
          // Soft: don't block UX if list route is not live yet
          if (res && res.message && !/404|not found|not implemented/i.test(String(res.message))) {
            this.errorMessage = res.message
          }
        }
      } catch (e) {
        this.history = []
      } finally {
        this.loadingHistory = false
      }
    },
    openFilePicker() {
      this.errorMessage = ''
      this.successMessage = ''
      if (this.$refs.signedFileInput) {
        this.$refs.signedFileInput.value = ''
        this.$refs.signedFileInput.click()
      }
    },
    async onSignedFileSelected(event) {
      const file = event.target && event.target.files && event.target.files[0]
      if (!file) return
      if (file.type && file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) {
        this.errorMessage = 'Please upload a PDF file.'
        return
      }
      const sizeError = getInvoiceFileSizeError(file)
      if (sizeError) {
        this.errorMessage = sizeError
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'File too large',
            text: sizeError,
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        if (this.$refs.signedFileInput) this.$refs.signedFileInput.value = ''
        return
      }
      await this.uploadSigned(file)
    },
    async uploadSigned(file) {
      const id = this.resolvedInvoiceId
      if (!id) {
        this.errorMessage = 'Invoice id missing — cannot upload.'
        return
      }
      const sizeError = getInvoiceFileSizeError(file)
      if (sizeError) {
        this.errorMessage = sizeError
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'File too large',
            text: sizeError,
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
        return
      }
      this.uploading = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        const res = await enhancedInvoiceService.uploadSignedInvoice(id, file)
        if (res && (res.status === 'OK' || res.status === 200)) {
          this.successMessage = 'Signed invoice uploaded and saved for this invoice.'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Signed invoice saved',
              text: 'File stored on the server for this invoice.',
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          })
          this.$emit('signed-uploaded', res.data || res)
          await this.loadHistory()
        } else {
          const msg = (res && (res.message || (res.data && res.data.message)))
            || 'Upload failed. Backend signed-invoice API is required.'
          this.errorMessage = msg
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Upload failed',
              text: msg,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
        }
      } catch (e) {
        this.errorMessage = 'Upload failed. Please try again.'
      } finally {
        this.uploading = false
      }
    },
    async downloadInvoice() {
      if (!this.canDownload) {
        this.errorMessage = INVOICE_DOWNLOAD_DENIED_TOOLTIP
        return
      }
      this.downloading = true
      this.errorMessage = ''
      try {
        // 1) Prefer parent-provided client PDF (create wizard still has preview DOM)
        if (typeof this.clientPdfProvider === 'function') {
          const result = await this.clientPdfProvider()
          if (result && result.blob && this.resolvedInvoiceId) {
            await this.persistGeneratedPdf(result.blob, result.filename)
          }
          return
        }

        const downloadName = buildInvoiceDownloadFilename(
          this.invoice || { invoice_id: this.resolvedInvoiceId },
        )

        // 2) Server PDF endpoint
        const pdfRes = await enhancedInvoiceService.downloadInvoicePdf(this.resolvedInvoiceId)
        if (pdfRes && pdfRes.status === 'OK' && pdfRes.blob) {
          this.triggerBrowserDownload(pdfRes.blob, downloadName)
          return
        }

        // 3) Existing stored file path on the invoice row (legacy)
        const path = this.invoice
          && (this.invoice.generated_pdf_path
            || this.invoice.generated_pdf
            || this.invoice.file
            || this.invoice.file_path)
        if (path && typeof path === 'string') {
          const key = path.replace(/^\//, '')
          const fileRes = await JobService.getFileBase64(key)
          if (fileRes && fileRes.file) {
            const byteChars = atob(fileRes.file)
            const bytes = new Uint8Array(byteChars.length)
            for (let i = 0; i < byteChars.length; i += 1) bytes[i] = byteChars.charCodeAt(i)
            const blob = new Blob([bytes], { type: 'application/pdf' })
            this.triggerBrowserDownload(blob, downloadName)
            return
          }
        }

        this.errorMessage = (pdfRes && pdfRes.message)
          || 'No downloadable PDF on the server yet. Generate/download from the create preview, or implement GET /invoice/:id/pdf.'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'PDF not available',
            text: this.errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
      } catch (e) {
        this.errorMessage = 'Could not download invoice PDF.'
      } finally {
        this.downloading = false
      }
    },
    async persistGeneratedPdf(blob, filename) {
      try {
        const sizeError = getInvoiceFileSizeError(blob)
        if (sizeError) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'File too large',
              text: sizeError,
              icon: 'AlertCircleIcon',
              variant: 'warning',
            },
          })
          return
        }
        const file = new File(
          [blob],
          filename || buildInvoiceDownloadFilename(
            this.invoice || { invoice_id: this.resolvedInvoiceId },
          ),
          { type: 'application/pdf', lastModified: Date.now() },
        )
        const res = await enhancedInvoiceService.uploadGeneratedPdf(this.resolvedInvoiceId, file, {
          source: 'client_preview_download',
        })
        if (!(res && (res.status === 'OK' || res.status === 200))) {
          // Non-fatal: user still got their download
          console.warn('[SignedInvoiceWorkflow] generated PDF persist failed', res)
        } else {
          this.$emit('generated-pdf-stored', res.data || res)
        }
      } catch (e) {
        console.warn('[SignedInvoiceWorkflow] generated PDF persist error', e)
      }
    },
    triggerBrowserDownload(blob, filename) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
        || buildInvoiceDownloadFilename(
          this.invoice || { invoice_id: this.resolvedInvoiceId },
        )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    refresh() {
      return this.loadHistory()
    },
  },
}
</script>

<style scoped>
.signed-invoice-workflow {
  border: 1px solid #e0e0e0;
  border-radius: 0.35rem;
  padding: 0.85rem 1rem;
  background: #fafbfc;
}
.signed-invoice-history li:last-child {
  border-bottom: 0 !important;
}
</style>
